/**
 * Reusable question renderer (CR3). One component renders all three question
 * types (scale 1–5 / choice / open) used by the PIC form (picq), the Owner
 * verification round (ownerq), and — read-only — the internal review editor.
 *
 * It is a controlled field: the parent owns the value and is notified on every
 * change so it can drive debounced autosave. The `value` shape mirrors the API
 * AnswerInput contract:
 *   - scale  → number | null   (1..5)
 *   - choice → string | null   (the chosen option label)
 *   - open   → string          (free text)
 *
 * Motion is purposeful (selected-state spring on the scale chips) and gated on
 * useReducedMotion. Lucide icons carry aria-labels; the field group is labelled
 * for screen readers.
 */
import { motion, useReducedMotion } from 'framer-motion';
import type { QuestionDTO } from '@klinik/shared';
import { Badge } from './ui/Badge';
import { SPRING_SOFT } from '../lib/motion';

export type AnswerValue = string | number | null;

/** Coerce an unknown saved value into the shape this field expects per type. */
export function normalizeAnswerValue(type: QuestionDTO['type'], value: unknown): AnswerValue {
  if (type === 'scale') {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
    return null;
  }
  if (type === 'choice') return typeof value === 'string' && value !== '' ? value : null;
  // open
  return typeof value === 'string' ? value : '';
}

/** A non-empty answer per the API's isEmptyValue semantics (0 is valid for scale). */
export function isAnswered(type: QuestionDTO['type'], value: AnswerValue): boolean {
  if (type === 'scale') return typeof value === 'number';
  if (type === 'choice') return typeof value === 'string' && value !== '';
  return typeof value === 'string' && value.trim() !== '';
}

const SCALE_POINTS = [1, 2, 3, 4, 5] as const;

export function QuestionField({
  question,
  index,
  value,
  onChange,
  disabled = false,
  readOnly = false,
}: {
  question: QuestionDTO;
  /** 1-based display number. */
  index: number;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  disabled?: boolean;
  /** Review surface: render the control inert (no inputs), for preview only. */
  readOnly?: boolean;
}) {
  const reduce = useReducedMotion();
  const inputId = `q-${question.id}`;
  const inert = disabled || readOnly;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-soft">
      <div className="flex items-start gap-3">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-blue-600 text-xs font-bold text-white">
          {index}
        </span>
        <div className="flex-1">
          <label htmlFor={question.type === 'open' ? inputId : undefined} className="block text-sm font-medium text-slate-700">
            {question.text}
            {question.required && (
              <span className="ml-1 text-red-500" aria-hidden>
                *
              </span>
            )}
          </label>
          {question.required && <span className="sr-only">wajib diisi</span>}
        </div>
      </div>

      <div className="mt-3 pl-9">
        {question.type === 'scale' && (
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label={`Skala 1 sampai 5 untuk pertanyaan ${index}`}
          >
            {SCALE_POINTS.map((n) => {
              const selected = value === n;
              return (
                <motion.button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={`Nilai ${n}`}
                  disabled={inert}
                  onClick={() => onChange(selected ? null : n)}
                  whileTap={reduce || inert ? undefined : { scale: 0.92 }}
                  transition={SPRING_SOFT}
                  className={`grid h-10 w-10 place-items-center rounded-lg border text-sm font-semibold transition-colors disabled:cursor-default ${
                    selected
                      ? 'border-blue-600 bg-blue-50 font-bold text-blue-700'
                      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                  }`}
                >
                  {n}
                </motion.button>
              );
            })}
            <div className="ml-1 hidden items-center text-[11px] text-slate-400 sm:flex">
              1 = sangat rendah · 5 = sangat tinggi
            </div>
          </div>
        )}

        {question.type === 'choice' && (
          <div className="space-y-2" role="radiogroup" aria-label={`Pilihan untuk pertanyaan ${index}`}>
            {(question.options ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-400">
                Belum ada opsi jawaban.
              </div>
            ) : (
              (question.options ?? []).map((opt) => {
                const selected = value === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={inert}
                    onClick={() => onChange(selected ? null : opt)}
                    className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default ${
                      selected
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${
                        selected ? 'border-blue-600' : 'border-slate-300'
                      }`}
                      aria-hidden
                    >
                      {selected && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                    </span>
                    <span>{opt}</span>
                  </button>
                );
              })
            )}
          </div>
        )}

        {question.type === 'open' && (
          <textarea
            id={inputId}
            rows={3}
            disabled={inert}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={readOnly ? '' : 'Ketik jawaban Anda…'}
            className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white disabled:cursor-default disabled:opacity-70"
          />
        )}
      </div>

      {readOnly && (
        <div className="mt-2 pl-9">
          <Badge tone="draft">Pratinjau · {scaleLabel(question.type)}</Badge>
        </div>
      )}
    </div>
  );
}

function scaleLabel(type: QuestionDTO['type']): string {
  if (type === 'scale') return 'Skala 1–5';
  if (type === 'choice') return 'Pilihan';
  return 'Isian terbuka';
}
